import { useEffect } from "react";

export type AuthURLParams = {
  sessionId?: string | null;
  correlationId?: string | null;
  serviceBaseUrl?: string | null;
  launchSource?: string | null;
  tenantId?: string | null;
};

export interface Auth {
  isAuthSession: boolean;
  sessionId: string | null;
  correlationId: string | null;
  serviceBaseUrl: string | null;
  launchSource: string | null;
  tenantId: string | null;
}

/**
 * This hook gets the session context information if present from the sessionStorage.
 * @returns auth session object.
 */
export const useAuth: () => Auth = () => {
  // Refetch the session storage variables upon refresh
  const sessionId = sessionStorage.getItem("sessionId") ?? null;
  const correlationId = sessionStorage.getItem("cid") ?? null;
  const serviceBaseUrl = sessionStorage.getItem("serviceBaseUrl") ?? null;
  const launchSource = sessionStorage.getItem("launchSource") ?? null;
  const tenantId = sessionStorage.getItem("tid") ?? null;

  /*
  Session Expire logic all enclosed in useEffect
  This keeps track of the 30 minute expiration of the Session Id value
  which is passed through the url parameters. The session expire time
  is saved in the session storage to persist upon refresh.
  The timer is set for 29 min, when finished redirects user to the
  Timeout page and clears the session storage
  */
  useEffect(() => {
    // 1. Get the Session Expire time in session storage
    const sessionExpireTime = sessionStorage.getItem("sessionExpireTime");

    // 2. If session not Expired, create a new timeout from current time until expiration
    if (sessionExpireTime) {
      const newTimeoutValue = +sessionExpireTime - new Date().getTime();

      const newTimer = setTimeout(() => {
        // When timeout executes, clear session storage and redirect to Timeout error page
        sessionStorage.clear();
        window.location.pathname = "/timeout";
      }, newTimeoutValue);

      return () => clearTimeout(newTimer);
    }
  }, []);

  return {
    isAuthSession: sessionId ? true : false,
    sessionId,
    correlationId,
    serviceBaseUrl,
    launchSource,
    tenantId,
  };
};

/*
 * This function initializes the session context in the session storage to keep the app state persistent
 * set the authSession variables. This session context is passed in after being taken from the URL parameters
 * upon launching the app
 */
export const setAuthSessionVariables = (authParams: AuthURLParams) => {
  // The session context is only set if a session Id is passed
  if (authParams?.sessionId) {
    sessionStorage.setItem("sessionId", authParams?.sessionId ?? "");
    sessionStorage.setItem("cid", authParams?.correlationId ?? "");
    sessionStorage.setItem("serviceBaseUrl", authParams?.serviceBaseUrl ?? "");
    sessionStorage.setItem("tid", authParams?.tenantId ?? "");
    sessionStorage.setItem("launchSource", authParams?.launchSource ?? "");

    // Store the time the session context was initially set
    const sessionStartTime = new Date().getTime();
    const sessionExpireTime = sessionStartTime + 1000 * 60 * 29; // 1000ms + 60sec + 29min

    sessionStorage.setItem("sessionExpireTime", sessionExpireTime.toString());
  }
};
